import type { DocumentCategory } from "../documents";
import type { Foutmelding } from "../utils/foutmelding";
import type { CdZip } from "../components/dossier/cdZip";

/** Een mislukt bestand: naam plus de melding voor het scherm (B12). */
export interface UploadFout {
  naam: string;
  melding: Foutmelding;
  /**
   * Zoveel bestanden dekt deze fout (standaard 1): één melding voor een batch
   * die niet in het register kon (trein 51, L4).
   */
  aantal?: number;
}

/** Hoeveel bestanden er mislukten, met `aantal` meegeteld. */
export const aantalMislukt = (errors: UploadFout[]): number =>
  errors.reduce((n, f) => n + (f.aantal ?? 1), 0);

/** Wat het register van een eerdere poging weet (I18, WP3): de Graph-sessie en het gekozen pad. */
export interface HervatStand {
  uploadUrl: string | null;
  blobName: string | null;
}

interface UploadBasis {
  naam: string;
  category: DocumentCategory;
  /** Pad zoals gesleept of gekozen; daarop koppelt een hervatting het bestand terug. */
  relatiefPad?: string;
  /** Id in het uploadregister; bekend bij een hervatte batch. */
  bestandId?: string;
  hervat?: HervatStand;
}

/** Volledig voorbereide upload zoals de batchmodal die aflevert. */
export interface BestandUpload extends UploadBasis {
  file: File;
  targetFolder?: string;
}

/** Een cd-map die de browser als één zip uploadt (D1). */
export interface CdUpload extends UploadBasis {
  cd: CdZip;
  targetFolder: string;
}

export type QueuedUploadItem = BestandUpload | CdUpload;

export function grootteVan(item: QueuedUploadItem): number {
  return "cd" in item ? item.cd.grootte : item.file.size;
}

/**
 * lastModified van één bestand, nooit negatief: een datum vóór 1970 (kapotte
 * tijdstempel van een oude cd of camera) wordt 0, anders weigert de server de
 * hele registratie van tot 200 bestanden (A2-5).
 */
export function wijzigdatumVanBestand(file: File): number {
  return Math.max(0, file.lastModified || 0);
}

/** De laatste lastModified van een reeks bestanden (0 als die ontbreekt). */
export function laatsteWijziging(bestanden: { file: File }[]): number {
  return bestanden.reduce(
    (m, b) => Math.max(m, wijzigdatumVanBestand(b.file)),
    0,
  );
}

/**
 * Wijzigdatum voor het register (L8): van het bestand, bij een cd de laatste
 * van zijn bestanden. Hervatten in een oude sessie mag alleen als die gelijk is.
 */
export function wijzigdatumVan(item: QueuedUploadItem): number {
  return "cd" in item
    ? laatsteWijziging(item.cd.bestanden)
    : wijzigdatumVanBestand(item.file);
}

/** Zoveel bestanden registreert de webapp per batch (JSON-limiet van de server). */
export const MAX_BESTANDEN_PER_BATCH = 200;
/**
 * En zoveel bytes JSON per registratie: de server neemt hooguit 100 KB per
 * verzoek aan, en lange mappaden maken 200 bestanden al snel groter (F2).
 */
export const MAX_REGISTRATIE_BYTES = 60 * 1024;

/** Lengte van de JSON van een waarde in UTF-8-bytes (zonder TextEncoder, dat jsdom mist). */
export function jsonBytes(waarde: unknown): number {
  const s = JSON.stringify(waarde) ?? "";
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 0x80) n += 1;
    else if (c < 0x800) n += 2;
    else if (c >= 0xd800 && c <= 0xdbff) {
      n += 4; // surrogaatpaar: één teken van vier bytes
      i++;
    } else n += 3;
  }
  return n;
}

/**
 * Deelt items op in stukken van hooguit `max`, en (met `gewicht`) zo dat een
 * stuk de `maxGewicht` niet passeert. Een item dat alleen al te zwaar is,
 * krijgt een eigen stuk.
 */
export function verdeelInBatches<T>(
  items: T[],
  max = MAX_BESTANDEN_PER_BATCH,
  gewicht: (item: T) => number = () => 0,
  maxGewicht = MAX_REGISTRATIE_BYTES,
): T[][] {
  const delen: T[][] = [];
  let huidig: T[] = [];
  let som = 0;
  for (const item of items) {
    const g = gewicht(item);
    if (huidig.length > 0 && (huidig.length >= max || som + g > maxGewicht)) {
      delen.push(huidig);
      huidig = [];
      som = 0;
    }
    huidig.push(item);
    som += g;
  }
  if (huidig.length > 0) delen.push(huidig);
  return delen;
}

/** Een batch in het register die niet af is en hier niet loopt, bv. na herladen. */
export interface OnderbrokenBatch {
  dossierKenmerk: string;
  batchId: string;
  /** Bestanden die nog moeten. */
  open: number;
  /** Er zit een cd-zip bij. */
  heeftCd: boolean;
  /** Uitleg na een mislukte poging om te hervatten. */
  melding?: string;
}

/**
 * Twee banen, elk met één lopende batch: een grote upload (bv. een zip van
 * 20 GB) houdt zo een paar losse bestanden voor een ander dossier niet uren
 * vast (incident 11-10-2026: de batches achter een CD-upload verdwenen).
 */
export type Baan = "groot" | "normaal";
export const BANEN: Baan[] = ["groot", "normaal"];

/**
 * Eén bestand (of cd-zip) groter dan dit: de grote baan. Alleen de grootte
 * telt: een cd is sinds WP3 één zip met een bekende grootte, en vijftig
 * kleine pdf's horen niet achter een cd te wachten.
 */
export const GROOT_BESTAND_BYTES = 100 * 1024 * 1024;

export function baanVoor(items: QueuedUploadItem[]): Baan {
  return items.some((i) => grootteVan(i) > GROOT_BESTAND_BYTES)
    ? "groot"
    : "normaal";
}

/** Batch zoals de sectie hem aanbiedt. */
export interface NieuweBatch {
  id: number;
  dossierKenmerk: string;
  items: QueuedUploadItem[];
  /** Id in het uploadregister: al bekend bij een hervatte batch. */
  batchId?: string;
  aangemaaktOp?: string;
}

/** Batch die op zijn beurt wacht, met de baan waarin hij loopt. */
export interface WachtendeBatch extends NieuweBatch {
  baan: Baan;
}

/** De lopende batch van een baan: de wachtende plus voortgang. */
export interface ActieveBatch extends WachtendeBatch {
  done: number;
  current: string | null;
  /** 0..1 van het lopende bestand, voor een vloeiende voortgangsring. */
  currentFraction: number;
  errors: UploadFout[];
}

export interface BaanState {
  actief: ActieveBatch | null;
  wachtrij: WachtendeBatch[];
}

export interface UploadCompletedInfo {
  dossierKenmerk: string;
  total: number;
  errors: UploadFout[];
  at: number;
  /** Een ander tabblad nam de batch over en maakt hem af (B1c-low). */
  elders?: true;
}

export interface UploadQueueReducerState {
  banen: Record<Baan, BaanState>;
  /**
   * De sessie is verlopen (401): de lopende uploads wachten tot er weer is
   * ingelogd en gaan dan verder met hetzelfde bestand. Niets wordt als
   * mislukt geteld.
   */
  gepauzeerd: boolean;
  lastCompleted: UploadCompletedInfo | null;
  /** Batches in het register die niet af zijn en hier niet lopen (I18, WP3). */
  onderbroken: OnderbrokenBatch[];
}

export type UploadQueueAction =
  | { type: "ENQUEUE"; batch: NieuweBatch }
  | { type: "VOORTGANG"; baan: Baan; fraction: number }
  | { type: "ITEM_KLAAR"; baan: Baan; fout?: UploadFout; aantal?: number }
  | { type: "BATCH_KLAAR"; baan: Baan; at: number; elders?: boolean }
  | { type: "PAUZE" }
  | { type: "HERVAT" }
  | { type: "ONDERBROKEN"; lijst: OnderbrokenBatch[] }
  | { type: "ONDERBROKEN_WEG"; batchId: string };

const LEGE_BAAN: BaanState = { actief: null, wachtrij: [] };

export const LEGE_WACHTRIJ_STATE: UploadQueueReducerState = {
  banen: { groot: LEGE_BAAN, normaal: LEGE_BAAN },
  gepauzeerd: false,
  lastCompleted: null,
  onderbroken: [],
};

function activeer(batch: WachtendeBatch): ActieveBatch {
  return {
    ...batch,
    done: 0,
    current: batch.items[0]?.naam ?? null,
    currentFraction: 0,
    errors: [],
  };
}

function metBaan(
  state: UploadQueueReducerState,
  baan: Baan,
  nieuw: BaanState,
): UploadQueueReducerState {
  return { ...state, banen: { ...state.banen, [baan]: nieuw } };
}

/**
 * Per baan een FIFO van upload-batches: één batch loopt, de rest wacht in
 * volgorde van aanbieden (ook batches van een ander dossier). Puur, zodat de
 * volgorde- en afrondregels los van React te testen zijn; de uploadlus zelf
 * zit in UploadQueueContext.
 */
export function uploadQueueReducer(
  state: UploadQueueReducerState,
  action: UploadQueueAction,
): UploadQueueReducerState {
  switch (action.type) {
    case "ENQUEUE": {
      if (action.batch.items.length === 0) return state;
      const batch: WachtendeBatch = {
        ...action.batch,
        baan: baanVoor(action.batch.items),
      };
      const huidig = state.banen[batch.baan];
      const onderbroken = batch.batchId
        ? state.onderbroken.filter((o) => o.batchId !== batch.batchId)
        : state.onderbroken;
      return metBaan(
        { ...state, onderbroken },
        batch.baan,
        huidig.actief
          ? { ...huidig, wachtrij: [...huidig.wachtrij, batch] }
          : { ...huidig, actief: activeer(batch) },
      );
    }
    case "VOORTGANG": {
      const huidig = state.banen[action.baan];
      if (!huidig.actief) return state;
      return metBaan(state, action.baan, {
        ...huidig,
        actief: { ...huidig.actief, currentFraction: action.fraction },
      });
    }
    case "ITEM_KLAAR": {
      const huidig = state.banen[action.baan];
      if (!huidig.actief) return state;
      const done = huidig.actief.done + (action.aantal ?? 1);
      return metBaan(state, action.baan, {
        ...huidig,
        actief: {
          ...huidig.actief,
          done,
          current: huidig.actief.items[done]?.naam ?? null,
          currentFraction: 0,
          errors: action.fout
            ? [...huidig.actief.errors, action.fout]
            : huidig.actief.errors,
        },
      });
    }
    case "BATCH_KLAAR": {
      const huidig = state.banen[action.baan];
      if (!huidig.actief) return state;
      const [volgende, ...rest] = huidig.wachtrij;
      return {
        ...metBaan(state, action.baan, {
          actief: volgende ? activeer(volgende) : null,
          wachtrij: rest,
        }),
        lastCompleted: {
          dossierKenmerk: huidig.actief.dossierKenmerk,
          total: huidig.actief.items.length,
          errors: huidig.actief.errors,
          at: action.at,
          ...(action.elders ? { elders: true as const } : {}),
        },
      };
    }
    case "PAUZE":
      return state.gepauzeerd ? state : { ...state, gepauzeerd: true };
    case "HERVAT":
      return state.gepauzeerd ? { ...state, gepauzeerd: false } : state;
    case "ONDERBROKEN":
      return { ...state, onderbroken: action.lijst };
    case "ONDERBROKEN_WEG":
      return {
        ...state,
        onderbroken: state.onderbroken.filter(
          (o) => o.batchId !== action.batchId,
        ),
      };
    default:
      return state;
  }
}

/** Een upload loopt of wacht nog, in welke baan dan ook. */
export function isBezig(state: UploadQueueReducerState): boolean {
  return BANEN.some(
    (b) => state.banen[b].actief !== null || state.banen[b].wachtrij.length > 0,
  );
}
